import { gql } from "@apollo/client";
import EditIcon from "@mui/icons-material/Edit";
import { Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, Switch, TableCell, TableRow, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { useCreatePrecautionMutation, useUpdatePrecautionMutation } from "~/graphql/__generated__/graphql";
import { OrgConfig } from "~/providers/OrgConfigProvider";
import { PRECAUTION_COLOR_PALETTE } from "./precautionColors";
import ColorPicker from "~/components/common/forms/ColorPicker";
import PrecautionOctagon from "~/components/patient/PrecautionOctagon";

type Precaution = NonNullable<OrgConfig["precautions"]["string"]>

interface Props {
  precaution: Precaution
}

const PrecautionRow = ({ precaution }: Props) => {
  const [active, setActive] = useState(precaution.active);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [update, { loading }] = useUpdatePrecautionMutation();

  return <TableRow>
    <TableCell
      sx={{
        width: '50%',
        maxWidth: '400px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <PrecautionOctagon color={precaution.color} size={16} sx={{ mr: 1 }} />
        <span>{precaution.name}</span>
        <IconButton
          size="small"
          aria-label="Edit precaution"
          onClick={() => setDialogOpen(true)}
          sx={{ minWidth: 'auto', padding: '4px' }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </div>
    </TableCell>
    <TableCell sx={{ width: '20%', maxWidth: '150px' }}>
      {precaution.description || <Typography variant="body2" color="text.secondary">—</Typography>}
    </TableCell>
    <TableCell sx={{ width: '15%', maxWidth: '120px' }}>
      {active ?
        <Chip label="Active" color="success" size="small" />
      :
        <Chip label="Inactive" size="small" />
      }
    </TableCell>
    <TableCell sx={{ width: '15%', maxWidth: '100px' }}>
      <Switch checked={active} disabled={loading} onChange={() => {
        setActive(!active)
        update({ variables: { ...precaution, active: !active }})
      }} size="small" />
    </TableCell>

    {dialogOpen && <PrecautionDialog onClose={() => setDialogOpen(false)} precaution={precaution} />}
  </TableRow>
}

export default PrecautionRow;

interface PrecautionDialogProps {
  onClose: () => void;
  precaution: Precaution;
}

export const PrecautionDialog = ({
  onClose,
  precaution,
}: PrecautionDialogProps) => {
  const [local, setLocal] = useState(precaution);
  const [create, { loading: createLoading }] = useCreatePrecautionMutation();
  const [update, { loading: updateLoading }] = useUpdatePrecautionMutation();

  const loading = createLoading || updateLoading;
  const isEditing = !!local.id;

  const handleSave = async () => {
    if (isEditing) {
      await update({ variables: { ...local, description: local.description || null } })
    } else {
      await create({
        variables: { name: local.name, color: local.color, description: local.description || null },
        awaitRefetchQueries: true,
        refetchQueries: ["OrgConfig"],
      })
    }

    onClose();
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditing ? 'Edit Precaution' : 'Add Precaution'}
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Name"
          fullWidth
          variant="outlined"
          value={local.name}
          onChange={(e) => setLocal({ ...local, name: e.target.value })}
          disabled={loading}
          sx={{ mb: 2 }}
        />
        <TextField
          margin="dense"
          label="Description (optional)"
          fullWidth
          multiline
          minRows={2}
          variant="outlined"
          value={local.description || ""}
          onChange={(e) => setLocal({ ...local, description: e.target.value })}
          disabled={loading}
          sx={{ mb: 2 }}
        />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Color</Typography>
        <ColorPicker
          value={local.color}
          onChange={(color) => setLocal({ ...local, color })}
          palette={PRECAUTION_COLOR_PALETTE}
          disabled={loading}
          sx={{ mb: 1 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={local.active}
              onChange={(e) => setLocal({ ...local, active: e.target.checked })}
              disabled={loading}
            />
          }
          label="Active"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          loading={loading}
          disabled={!local.name.trim() || !local.color}
        >
          {loading ? (isEditing ? 'Updating...' : 'Creating...') : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

gql`
  mutation CreatePrecaution($name: String!, $color: String!, $description: String) @permission(requires: manageOrgConfig) {
    createPrecaution(name: $name, color: $color, description: $description) {
      id
    }
  }
`;

gql`
  mutation UpdatePrecaution($id: Int!, $name: String!, $color: String!, $description: String, $active: Boolean!) @permission(requires: manageOrgConfig) {
    updatePrecaution(id: $id, name: $name, color: $color, description: $description, active: $active) {
      id
      name
      color
      description
      active
    }
  }
`;
